package com.shadowings.gero.data.lessons

import com.shadowings.gero.data.lessons.chapter11.determineGenitiveDetails
import com.shadowings.gero.data.lessons.chapter6.conjugateVerbDe
import com.shadowings.gero.data.lessons.chapter6.conjugateVerbEn
import com.shadowings.gero.data.lessons.chapter6.conjugateVerbEs
import com.shadowings.gero.data.lessons.chapter6.conjugateVerbFr
import com.shadowings.gero.data.lessons.chapter6.conjugateVerbIt
import com.shadowings.gero.data.repository.SentenceOptions
import com.shadowings.gero.data.repository.SentenceResult
import com.shadowings.gero.data.repository.DatabaseRepositoryImpl
import com.shadowings.gero.data.repository.PossessivePairs
import com.shadowings.gero.data.repository.AdjectivePairs
import com.shadowings.gero.domain.model.IQuizContent
import com.shadowings.gero.domain.model.PhoneticKeyboardContent
import com.shadowings.gero.domain.model.WordSelectionContent
import com.shadowings.gero.presentation.model.Verb
import com.shadowings.gero.presentation.model.Word
import kotlin.random.Random

// buildImperfectiveSentenceSync itself throws if no row satisfies the
// requested counts (it's meant to be a programming error for callers that
// know their pool is valid). The VSO quiz builders below are called with a
// pool that can legitimately be empty or too sparse - e.g. SentenceRepository
// .getSeenFilteredVerbSentences() for a player who hasn't unlocked enough
// vocabulary yet - so they check first and just produce fewer (possibly
// zero) quizzes instead of crashing.
// Checked against distinct base words, not raw list size: a row like
// FALCO,FALCHI CACCIARE GATTO,PESCE,UCCELLO,UCCELLI only has ONE distinct
// subject root even though `subjects.size` is 2, so soggettiCount=2 must not
// be considered satisfiable by it - otherwise buildImperfectiveSentenceSync
// has no way to honor the count without repeating the same word's singular
// and plural together (e.g. "il falco e i falchi").
private fun hasVsoCandidate(
    soggettiCount: Int,
    oggettiCount: Int,
    verbSentences: List<SentenceOptions>,
    words: List<Word>
): Boolean = verbSentences.any {
    distinctBaseWordCount(it.subjects, words) >= soggettiCount &&
        distinctBaseWordCount(it.objects, words) >= oggettiCount
}

fun buildImperfectiveSentenceSync(
    soggettiCount: Int,
    oggettiCount: Int,
    random: Random,
    verbSentences: List<SentenceOptions> = DatabaseRepositoryImpl.verbSentences,
    words: List<Word> = DatabaseRepositoryImpl.words,
    verbs: List<Verb> = DatabaseRepositoryImpl.verbs,
    isNegative: Boolean = false,
    thisSubject: Boolean = false,
    subjectHavePossessive: Boolean = false,
    possessivePairs: List<PossessivePairs> = DatabaseRepositoryImpl.possessivePairs,
    objectHavePossessive: Boolean = false,
    objectPossessorHavePossessive: Boolean = false,
    // An adjective on the first subject or the first object, whichever can take one and has no
    // possessive (wnm sꜣ.i t nfr "mio figlio mangia il pane buono"); none if neither can.
    haveAdjective: Boolean = false,
    adjectivePairs: List<AdjectivePairs> = DatabaseRepositoryImpl.adjectivePairs
): SentenceResult {
    require(soggettiCount >= 1) { "soggettiCount deve essere >= 1" }
    require(oggettiCount >= 0) { "oggettiCount deve essere >= 0" }

    // Filtered on DISTINCT base words, not raw list size - a row where
    // soggetto only offers one root (e.g. FALCO,FALCHI) must not be treated
    // as satisfying soggettiCount=2, or the picks below would be forced to
    // pair a word with its own plural/singular (e.g. "il falco e i falchi").
    fun candidatesOf(rows: List<SentenceOptions>) = rows
        .filter {
            distinctBaseWordCount(it.subjects, words) >= soggettiCount &&
                distinctBaseWordCount(it.objects, words) >= oggettiCount
        }

    // With subjectHavePossessive / objectHavePossessive only subjects / objects that can take one
    // are drawn. If no row has one, that noun just goes without instead of failing.
    var rows = verbSentences
    var withSubjectPossessive = false
    if (subjectHavePossessive && !thisSubject) {
        val owned = withPossessableSubjects(rows, words, possessivePairs)
        if (candidatesOf(owned).isNotEmpty()) {
            rows = owned
            withSubjectPossessive = true
        }
    }
    // The first object gets an owner from the row's possessore dell'oggetto, with its own suffix
    // possessive (mw it.k "l'acqua di tuo padre"). It takes the place of objectHavePossessive.
    var withObjectOwner = false
    if (objectPossessorHavePossessive && oggettiCount > 0) {
        val owned = withPossessablePossessors(rows, words, possessivePairs)
        if (candidatesOf(owned).isNotEmpty()) {
            rows = owned
            withObjectOwner = true
        }
    }
    var withObjectPossessive = false
    if (objectHavePossessive && oggettiCount > 0 && !withObjectOwner) {
        val owned = withPossessableObjects(rows, words, possessivePairs)
        if (candidatesOf(owned).isNotEmpty()) {
            rows = owned
            withObjectPossessive = true
        }
    }
    val candidates = candidatesOf(rows)

    if (candidates.isEmpty()) {
        error("Nessuna riga in sentences_verbial.tsv soddisfa i criteri: soggetti=$soggettiCount, oggetti=$oggettiCount")
    }

    val chosenPattern = candidates[random.nextInt(candidates.size)]
    val patternSubjects = chosenPattern.subjects
    val verbKey = chosenPattern.verbs
    val patternObjects = chosenPattern.objects

    val shuffledSubjects = patternSubjects.shuffled(random)
    val shuffledObjects = patternObjects.shuffled(random)

    val usedBaseIds = mutableSetOf<String>()
    val chosenSubjectsKeys = pickDistinctIdsByBaseWord(shuffledSubjects, soggettiCount, words, usedBaseIds)
    val chosenObjectsKeys = pickDistinctIdsByBaseWord(shuffledObjects, oggettiCount, words, usedBaseIds)

    // The candidates filter above guarantees enough distinct roots within
    // each field on its own; this only remains false if a subject and an
    // object in the SAME row happen to share a base word, which would make
    // usedBaseIds cross-exclude one of them. Fail loudly rather than fall
    // back to a root-blind pick that could repeat a word (the original bug).
    check(chosenSubjectsKeys.size == soggettiCount && chosenObjectsKeys.size == oggettiCount) {
        "Riga scelta in sentences_verbial.tsv non fornisce soggetti/oggetti a radice distinta a sufficienza: " +
            "soggetti=$soggettiCount, oggetti=$oggettiCount, verbo=${verbKey.firstOrNull()}"
    }

    val chosenSubjects = chosenSubjectsKeys.map { id ->
        words.find { it.id == id } ?: error("Word subject con id $id non trovato")
    }
    val chosenObjects = chosenObjectsKeys.map { id ->
        words.find { it.id == id } ?: error("Word object con id $id non trovato")
    }
    val verb = verbs.find { it.chiaveWords == verbKey.first() }
        ?: error("Verb con chiaveWords $verbKey non trovato")
    // Drawn only when asked for, so the random stream of every other caller stays the same.
    val subjectPossessive = if (withSubjectPossessive) {
        pickPossessivePronoun(chosenSubjects.first(), words, possessivePairs, random)
    } else {
        null
    }
    val objectPossessive = if (withObjectPossessive) {
        pickPossessivePronoun(chosenObjects.first(), words, possessivePairs, random)
    } else {
        null
    }
    val objectOwner = if (withObjectOwner) {
        val possessed = chosenObjects.first()
        // Not a word already in the sentence ("il pastore caccia la vipera del suo pastore").
        words.filter { it.id in chosenPattern.possessors && (it.baseWordId ?: it.id) !in usedBaseIds }.randomOrNull(random)?.let { owner ->
            pickPossessivePronoun(owner, words, possessivePairs, random)?.let { pronoun ->
                val (isDirect, connector) = determineGenitiveDetails(possessed, owner, random = random)
                ObjectOwner(owner, pronoun, isDirect, connector.first, connector.second)
            }
        }
    } else {
        null
    }
    val (subjectAdjective, objectAdjective) = if (haveAdjective) {
        pickSentenceAdjective(
            chosenSubjects.first().takeIf { !thisSubject && subjectPossessive == null },
            chosenObjects.firstOrNull()?.takeIf { objectPossessive == null && objectOwner == null },
            words,
            adjectivePairs,
            random
        )
    } else {
        null to null
    }

    return buildImperfectiveSentenceFromWords(
        chosenSubjects,
        chosenObjects,
        verb,
        isNegative = isNegative,
        thisSubject = thisSubject,
        subjectPossessive = subjectPossessive,
        objectPossessive = objectPossessive,
        objectOwner = objectOwner,
        subjectAdjective = subjectAdjective,
        objectAdjective = objectAdjective,
        words = words
    )
}

// The owner of the first object, with its suffix possessive: a direct (mw it.k) or indirect
// (mw n it.k) genitive, as determineGenitiveDetails picks it.
data class ObjectOwner(
    val noun: Word,
    val possessive: Word,
    val isDirect: Boolean,
    val connector: String,
    val particleTransliteration: String
)

// Pure assembly: given already-chosen/resolved subjects, objects and verb,
// builds the VSO hieroglyphs/transliteration/translations. Split out of
// buildImperfectiveSentenceSync (mirrors buildAdverbialSentenceFromWords)
// so callers that already picked their own words/verb — e.g.
// SentenceRepository.buildVsoPhrase — don't have to re-implement this.
fun buildImperfectiveSentenceFromWords(
    subjects: List<Word>,
    objects: List<Word>,
    verb: Verb,
    isNegative: Boolean = false,
    thisSubject: Boolean = false,
    // Suffix pronoun owning the first subject (sꜣ.i "mio figlio"). Not with the demonstrative.
    subjectPossessive: Word? = null,
    // Suffix pronoun owning the first object (t.f "il suo pane").
    objectPossessive: Word? = null,
    objectOwner: ObjectOwner? = null,
    // Adjective after the first subject / first object (sꜣ nfr "il figlio buono"). Never on a
    // noun with a possessive, the demonstrative or an owner.
    subjectAdjective: Word? = null,
    objectAdjective: Word? = null,
    // Only read for the verb's words.tsv flags (es_animal_object_no_a).
    words: List<Word> = DatabaseRepositoryImpl.words
): SentenceResult {
    require(subjects.isNotEmpty()) { "subjects non può essere vuoto" }
    val possessive = subjectPossessive?.takeUnless { thisSubject }
    require(subjectAdjective == null || (possessive == null && !thisSubject)) { "aggettivo e possessivo sullo stesso soggetto" }
    require(objectAdjective == null || (objects.isNotEmpty() && objectPossessive == null && objectOwner == null)) {
        "aggettivo e possessivo sullo stesso oggetto"
    }

    // 1) Hieroglyphs
    val verbHiero = if (verb.determinativo != "-") {
        "${verb.formaImperfettiva}-${verb.determinativo}"
    } else {
        verb.formaImperfettiva
    }
    val hieroParts = mutableListOf<String>()
    if (isNegative) {
        hieroParts.add("D35")
    }
    hieroParts.add(verbHiero)
    subjects.forEachIndexed { index, subject ->
        hieroParts.add(if (subjectAdjective != null && index == 0) adjectiveHieroglyphs(subject, subjectAdjective) else subject.gardiner)
        if (thisSubject && index == 0) {
            hieroParts.add(getDemonstrativeGardiner(subject))
        }
        if (possessive != null && index == 0) {
            hieroParts.add(possessive.gardiner)
        }
    }
    objects.forEachIndexed { index, obj ->
        if (objectOwner != null && index == 0) {
            val link = if (objectOwner.isDirect) "-" else objectOwner.connector
            hieroParts.add("${obj.gardiner}$link${objectOwner.noun.gardiner}")
            hieroParts.add(objectOwner.possessive.gardiner)
            return@forEachIndexed
        }
        hieroParts.add(if (objectAdjective != null && index == 0) adjectiveHieroglyphs(obj, objectAdjective) else obj.gardiner)
        if (objectPossessive != null && index == 0) {
            hieroParts.add(objectPossessive.gardiner)
        }
    }
    val hieroglyphs = hieroParts.joinToString("-")

    // 2) Transliteration
    val transParts = mutableListOf<String>()
    if (isNegative) {
        transParts.add("n")
    }
    transParts.add(verb.traslitterazioneImperfettiva)
    subjects.forEachIndexed { index, subject ->
        transParts.add(
            if (possessive != null && index == 0) "${subject.reading}.${possessive.reading}"
            else if (subjectAdjective != null && index == 0) adjectiveTransliteration(subject, subjectAdjective)
            else subject.reading
        )
        if (thisSubject && index == 0) {
            transParts.add(getDemonstrativeReading(subject))
        }
    }
    objects.forEachIndexed { index, obj ->
        if (objectOwner != null && index == 0) {
            transParts.add(obj.reading)
            if (!objectOwner.isDirect && objectOwner.particleTransliteration.isNotBlank()) {
                transParts.add(objectOwner.particleTransliteration)
            }
            transParts.add("${objectOwner.noun.reading}.${objectOwner.possessive.reading}")
            return@forEachIndexed
        }
        transParts.add(
            if (objectPossessive != null && index == 0) "${obj.reading}.${objectPossessive.reading}"
            else if (objectAdjective != null && index == 0) adjectiveTransliteration(obj, objectAdjective)
            else obj.reading
        )
    }
    val transliteration = transParts.joinToString(" ")

    // 3) Translation - conjugation setup
    val verbKey = verb.chiaveWords
    val animalIsThing = animalObjectIsThing(verbKey, words)
    val isSubjectPlural = subjects.size > 1 || subjects.any { it.isPlural }
    val pronounKey = if (isSubjectPlural) "ESSI" else "EGLI"

    // 4) Italian Translation
    val verbConjugatedIt = conjugateVerbIt(verbKey, pronounKey)
    val verbIt = if (isNegative) "non $verbConjugatedIt" else verbConjugatedIt
    val joinedSubjectsIt = joinListIt(subjects.mapIndexed { index, word ->
        if (thisSubject && index == 0) formatItalianDemonstrativeSubject(word)
        else if (possessive != null && index == 0) possessiveTranslation(word, possessive.id, "it")
        else if (subjectAdjective != null && index == 0) adjectivePhraseTranslation(word, subjectAdjective, "it").first()
        else formatItalianSubject(word)
    })
    fun ownedObject(index: Int, word: Word, languageCode: String, plain: (Word) -> String): String =
        if (objectOwner != null && index == 0) {
            "${plain(word)} ${possessiveGenitiveTranslation(objectOwner.noun, objectOwner.possessive.id, languageCode)}"
        } else if (objectPossessive != null && index == 0) {
            possessiveTranslation(word, objectPossessive.id, languageCode, asObject = true, animalIsThing = animalIsThing)
        } else if (objectAdjective != null && index == 0) {
            adjectivePhraseTranslation(word, objectAdjective, languageCode, asObject = true, animalIsThing = animalIsThing).first()
        } else {
            plain(word)
        }
    val joinedObjectsIt = joinListIt(objects.mapIndexed { index, word -> ownedObject(index, word, "it", ::formatItalianSubject) })
    val translationIt = buildString {
        append(joinedSubjectsIt)
        append(" ")
        append(verbIt)
        if (joinedObjectsIt.isNotEmpty()) {
            append(" ")
            append(joinedObjectsIt)
        }
    }

    // 5) English Translation
    val verbConjugatedEn = conjugateVerbEn(verbKey, pronounKey)
    val verbEn = if (isNegative) {
        val baseVerbEn = conjugateVerbEn(verbKey, "ESSI")
        if (isSubjectPlural) "do not $baseVerbEn" else "does not $baseVerbEn"
    } else {
        verbConjugatedEn
    }
    val joinedSubjectsEn = joinListEn(subjects.mapIndexed { index, word ->
        if (thisSubject && index == 0) formatEnglishDemonstrativeSubject(word)
        else if (possessive != null && index == 0) possessiveTranslation(word, possessive.id, "en")
        else if (subjectAdjective != null && index == 0) adjectivePhraseTranslation(word, subjectAdjective, "en").first()
        else formatEnglishSubject(word)
    })
    val joinedObjectsEn = joinListEn(objects.mapIndexed { index, word -> ownedObject(index, word, "en", ::formatEnglishSubject) })
    val englishObjectPhrase = formatEnglishObjectPhrase(verbKey, joinedObjectsEn)
    val translationEn = buildString {
        append(joinedSubjectsEn)
        append(" ")
        append(verbEn)
        if (englishObjectPhrase.isNotEmpty()) {
            append(" ")
            append(englishObjectPhrase)
        }
    }

    // 6) German Translation
    val verbConjugatedDe = conjugateVerbDe(verbKey, pronounKey, hasObject = objects.isNotEmpty())
    val joinedSubjectsDe = joinListDe(subjects.mapIndexed { index, word ->
        if (thisSubject && index == 0) formatGermanDemonstrativeSubject(word)
        else if (possessive != null && index == 0) possessiveTranslation(word, possessive.id, "de")
        else if (subjectAdjective != null && index == 0) adjectivePhraseTranslation(word, subjectAdjective, "de").first()
        else formatGermanSubject(word)
    })
    val joinedObjectsDe = joinListDe(objects.mapIndexed { index, word -> ownedObject(index, word, "de", ::formatGermanObject) })
    val translationDe = moveGermanSeparableParticleToEnd(buildString {
        append(joinedSubjectsDe)
        append(" ")
        append(verbConjugatedDe)
        if (joinedObjectsDe.isNotEmpty()) {
            append(" ")
            append(joinedObjectsDe)
        }
        if (isNegative) {
            append(" nicht")
        }
    })

    // 7) Spanish Translation
    val verbConjugatedEs = conjugateVerbEs(verbKey, pronounKey)
    val verbEs = if (isNegative) "no $verbConjugatedEs" else verbConjugatedEs
    val joinedSubjectsEs = joinListEs(subjects.mapIndexed { index, word ->
        if (thisSubject && index == 0) formatSpanishDemonstrativeSubject(word)
        else if (possessive != null && index == 0) possessiveTranslation(word, possessive.id, "es")
        else if (subjectAdjective != null && index == 0) adjectivePhraseTranslation(word, subjectAdjective, "es").first()
        else formatSpanishSubject(word)
    })
    val joinedObjectsEs = joinListEs(objects.mapIndexed { index, word -> ownedObject(index, word, "es") { formatSpanishObject(it, animalIsThing) } })
    val translationEs = buildString {
        append(joinedSubjectsEs)
        append(" ")
        append(verbEs)
        if (joinedObjectsEs.isNotEmpty()) {
            append(" ")
            append(joinedObjectsEs)
        }
    }

    // 8) French Translation
    val verbConjugatedFr = conjugateVerbFr(verbKey, pronounKey)
    val verbFr = if (isNegative) {
        val startsWithVowel = verbConjugatedFr.firstOrNull()?.lowercaseChar() in listOf('a', 'e', 'i', 'o', 'u', 'é', 'è', 'ê', 'h')
        val negPart = if (startsWithVowel) "n'" else "ne "
        "$negPart$verbConjugatedFr pas"
    } else {
        verbConjugatedFr
    }
    val joinedSubjectsFr = joinListFr(subjects.mapIndexed { index, word ->
        if (thisSubject && index == 0) formatFrenchDemonstrativeSubject(word)
        else if (possessive != null && index == 0) possessiveTranslation(word, possessive.id, "fr")
        else if (subjectAdjective != null && index == 0) adjectivePhraseTranslation(word, subjectAdjective, "fr").first()
        else formatFrenchSubject(word)
    })
    val joinedObjectsFr = joinListFr(objects.mapIndexed { index, word -> ownedObject(index, word, "fr", ::formatFrenchSubject) })
    val translationFr = buildString {
        append(joinedSubjectsFr)
        append(" ")
        append(verbFr)
        if (joinedObjectsFr.isNotEmpty()) {
            append(" ")
            append(joinedObjectsFr)
        }
    }

    fun alternativesIn(languageCode: String, sentence: String): List<String> =
        listOfNotNull(
            subjectAdjective?.let { adjectiveAlternatives(sentence, subjects.first(), it, languageCode, asObject = false) },
            objectAdjective?.let { adjectiveAlternatives(sentence, objects.first(), it, languageCode, asObject = true, animalIsThing = animalIsThing) }
        ).flatten()

    return SentenceResult(
        hieroglyphs = hieroglyphs,
        transliteration = transliteration,
        translationIt = translationIt,
        translationEn = translationEn,
        translationDe = translationDe,
        translationEs = translationEs,
        translationFr = translationFr,
        isNegative = isNegative,
        thisSubject = thisSubject,
        alternativeTranslations = mapOf(
            "it" to alternativesIn("it", translationIt),
            "en" to alternativesIn("en", translationEn),
            "de" to alternativesIn("de", translationDe),
            "es" to alternativesIn("es", translationEs),
            "fr" to alternativesIn("fr", translationFr)
        ).filterValues { it.isNotEmpty() },
        hasAdjective = subjectAdjective != null || objectAdjective != null
    )
}

private val poolIt = listOf(
    "il",
    "la",
    "l'",
    "i",
    "gli",
    "le",
    "e",
    "con",
    "da",
    "in",
    "su",
    "gatto",
    "falco",
    "pesce",
    "uccello",
    "uomo",
    "scriba",
    "servo",
    "pane",
    "frutto",
    "dattero",
    "acqua",
    "birra",
    "donna",
    "gatti",
    "falchi",
    "pesci",
    "uccelli",
    "uomini",
    "scribi",
    "servi",
    "pani",
    "frutti",
    "datteri",
    "acque",
    "donne",
    "prende",
    "prendono",
    "mangia",
    "mangiano",
    "beve",
    "bevono",
    "ama",
    "amano",
    "vede",
    "vedono",
    "ascolta",
    "ascoltano"
)

private val poolEn = listOf(
    "the",
    "a",
    "an",
    "and",
    "with",
    "from",
    "in",
    "on",
    "cat",
    "hawk",
    "fish",
    "bird",
    "man",
    "scribe",
    "servant",
    "bread",
    "fruit",
    "date",
    "water",
    "beer",
    "woman",
    "cats",
    "hawks",
    "birds",
    "men",
    "scribes",
    "servants",
    "fruits",
    "dates",
    "women",
    "takes",
    "take",
    "eats",
    "eat",
    "drinks",
    "drink",
    "loves",
    "love",
    "sees",
    "see",
    "listens",
    "listen"
)

private val poolDe = listOf(
    "der",
    "die",
    "das",
    "ein",
    "eine",
    "und",
    "mit",
    "von",
    "in",
    "auf",
    "Katze",
    "Falke",
    "Fisch",
    "Vogel",
    "Mann",
    "Schreiber",
    "Diener",
    "Brot",
    "Frucht",
    "Dattel",
    "Wasser",
    "Bier",
    "Frau",
    "Katzen",
    "Falken",
    "Fische",
    "Vögel",
    "Männer",
    "Schreiber",
    "Diener",
    "Brote",
    "Früchte",
    "Datteln",
    "Wasser",
    "Frauen",
    "nimmt",
    "nehmen",
    "isst",
    "essen",
    "trinkt",
    "trinken",
    "liebt",
    "lieben",
    "sieht",
    "sehen",
    "hört",
    "hören"
)

private val poolEs = listOf(
    "el",
    "la",
    "los",
    "las",
    "un",
    "una",
    "y",
    "con",
    "de",
    "en",
    "sobre",
    "gato",
    "halcón",
    "pez",
    "pájaro",
    "hombre",
    "escriba",
    "siervo",
    "pan",
    "fruto",
    "dátil",
    "agua",
    "cerveza",
    "mujer",
    "gatos",
    "halcones",
    "peces",
    "pájaros",
    "hombres",
    "escribas",
    "siervos",
    "panes",
    "frutos",
    "dátiles",
    "aguas",
    "mujeres",
    "toma",
    "toman",
    "come",
    "comen",
    "bebe",
    "beben",
    "ama",
    "aman",
    "ve",
    "ven",
    "escucha",
    "escuchan"
)

private val poolFr = listOf(
    "le",
    "la",
    "l'",
    "les",
    "un",
    "une",
    "et",
    "avec",
    "de",
    "dans",
    "sur",
    "chat",
    "faucon",
    "poisson",
    "oiseau",
    "homme",
    "scribe",
    "serviteur",
    "pain",
    "fruit",
    "datte",
    "eau",
    "bière",
    "femme",
    "chats",
    "faucons",
    "poissons",
    "oiseaux",
    "hommes",
    "scribes",
    "serviteurs",
    "pains",
    "fruits",
    "dattes",
    "eaux",
    "femmes",
    "prend",
    "prennent",
    "mange",
    "mangent",
    "boit",
    "boivent",
    "aime",
    "aiment",
    "voit",
    "voient",
    "écoute",
    "écoutent"
)

private fun tokenizeCorrectPhrase(correctPhrase: String): List<String> {
    return correctPhrase
        .replace("’", "'")
        .replace("'", "' ")
        .replace(" ,", ",")
        .trim()
        .split(Regex("\\s+"))
        .flatMap { token ->
            when {
                token.isBlank() -> emptyList()
                token.endsWith(",") && token.length > 1 -> listOf(token.dropLast(1), ",")
                else -> listOf(token)
            }
        }
}

private fun normalizeToken(token: String): String {
    return token.replace("’", "'")
        .replace(" '", "'")
        .replace("' ", "'")
        .trim()
        .lowercase()
}

fun buildVsoWordSelectionQuizzes(
    soggettiCount: Int,
    oggettiCount: Int,
    random: Random,
    languageCode: String,
    count: Int = 10,
    verbSentences: List<SentenceOptions> = DatabaseRepositoryImpl.verbSentences,
    words: List<Word> = DatabaseRepositoryImpl.words,
    verbs: List<Verb> = DatabaseRepositoryImpl.verbs
): List<IQuizContent> {
    if (!hasVsoCandidate(soggettiCount, oggettiCount, verbSentences, words)) return emptyList()
    val quizzes = mutableListOf<IQuizContent>()
    for (i in 0 until count) {
        var attempts = 0
        var quiz: WordSelectionContent? = null
        while (attempts < 5) {
            val sentenceResult = buildImperfectiveSentenceSync(
                soggettiCount = soggettiCount,
                oggettiCount = oggettiCount,
                random = random,
                verbSentences = verbSentences,
                words = words,
                verbs = verbs
            )
            val correctPhrase = when (languageCode) {
                "it" -> sentenceResult.translationIt
                "de" -> sentenceResult.translationDe
                "es" -> sentenceResult.translationEs
                "fr" -> sentenceResult.translationFr
                else -> sentenceResult.translationEn
            }

            // Check if this correct phrase is already in the list
            val isDuplicate =
                quizzes.any { (it as? WordSelectionContent)?.correctPhrase == correctPhrase }

            val correctTokens = tokenizeCorrectPhrase(correctPhrase)
            val pool = when (languageCode) {
                "it" -> poolIt
                "de" -> poolDe
                "es" -> poolEs
                "fr" -> poolFr
                else -> poolEn
            }
            val distractors = pool.filter { token ->
                val norm = normalizeToken(token)
                correctTokens.none { normalizeToken(it) == norm }
            }.shuffled(random).take(5)

            val wordBank = (correctTokens + distractors).shuffled(random)

            val candidateQuiz = WordSelectionContent(
                hieroglyphs = sentenceResult.hieroglyphs,
                correctPhrase = correctPhrase,
                words = wordBank,
                transliteration = sentenceResult.transliteration,
                soggettiCount = soggettiCount,
                oggettiCount = oggettiCount
            )

            quiz = candidateQuiz
            if (!isDuplicate) {
                break
            }
            attempts++
        }
        if (quiz != null) {
            quizzes.add(quiz)
        }
    }
    return quizzes
}

fun buildVsoRecapWordSelectionQuizzes(
    random: Random,
    languageCode: String,
    count: Int = 10,
    verbSentences: List<SentenceOptions> = DatabaseRepositoryImpl.verbSentences,
    words: List<Word> = DatabaseRepositoryImpl.words,
    verbs: List<Verb> = DatabaseRepositoryImpl.verbs
): List<IQuizContent> {
    val quizzes = mutableListOf<IQuizContent>()
    for (i in 0 until count) {
        var attempts = 0
        var quiz: WordSelectionContent? = null
        while (attempts < 5) {
            val soggettiCount = random.nextInt(1, 3) // 1 or 2 subjects
            val oggettiCount = random.nextInt(0, 3)  // 0, 1, or 2 objects
            if (!hasVsoCandidate(soggettiCount, oggettiCount, verbSentences, words)) {
                attempts++
                continue
            }
            val sentenceResult = buildImperfectiveSentenceSync(
                soggettiCount = soggettiCount,
                oggettiCount = oggettiCount,
                random = random,
                verbSentences = verbSentences,
                words = words,
                verbs = verbs
            )
            val correctPhrase = when (languageCode) {
                "it" -> sentenceResult.translationIt
                "de" -> sentenceResult.translationDe
                "es" -> sentenceResult.translationEs
                "fr" -> sentenceResult.translationFr
                else -> sentenceResult.translationEn
            }

            // Check if this correct phrase is already in the list
            val isDuplicate =
                quizzes.any { (it as? WordSelectionContent)?.correctPhrase == correctPhrase }

            val correctTokens = tokenizeCorrectPhrase(correctPhrase)
            val pool = when (languageCode) {
                "it" -> poolIt
                "de" -> poolDe
                "es" -> poolEs
                "fr" -> poolFr
                else -> poolEn
            }
            val distractors = pool.filter { token ->
                val norm = normalizeToken(token)
                correctTokens.none { normalizeToken(it) == norm }
            }.shuffled(random).take(5)

            val wordBank = (correctTokens + distractors).shuffled(random)

            val candidateQuiz = WordSelectionContent(
                hieroglyphs = sentenceResult.hieroglyphs,
                correctPhrase = correctPhrase,
                words = wordBank,
                transliteration = sentenceResult.transliteration,
                soggettiCount = soggettiCount,
                oggettiCount = oggettiCount
            )

            quiz = candidateQuiz
            if (!isDuplicate) {
                break
            }
            attempts++
        }
        if (quiz != null) {
            quizzes.add(quiz)
        }
    }
    return quizzes
}

fun getVerbBaseEn(verbKey: String): String {
    return when (verbKey) {
        "PARLARE" -> "speak"
        "VEDERE" -> "see"
        "SCRIVERE" -> "write"
        "MANGIARE" -> "eat"
        "DARE" -> "give"
        "ASCOLTARE" -> "listen"
        "DIRE" -> "say"
        "BERE" -> "drink"
        "USCIRE" -> "go out"
        "ENTRARE" -> "enter"
        "AMARE" -> "love"
        "ESISTERE" -> "exist"
        "VOLARE" -> "fly"
        "COLTIVARE" -> "cultivate"
        "CACCIARE" -> "hunt"
        "OFFRIRE" -> "offer"
        "TROVARE" -> "find"
        "FARE" -> "make"
        "PORTARE" -> "bring"
        "CONOSCERE" -> "know"
        "ANDARE" -> "go"
        "INVIARE" -> "send"
        "PRENDERE" -> "take"
        else -> verbKey.lowercase()
    }
}


fun buildVsoPhoneticKeyboardQuizzes(
    soggettiCount: Int,
    oggettiCount: Int,
    random: Random,
    languageCode: String,
    count: Int = 2,
    verbSentences: List<SentenceOptions> = DatabaseRepositoryImpl.verbSentences,
    words: List<Word> = DatabaseRepositoryImpl.words,
    verbs: List<Verb> = DatabaseRepositoryImpl.verbs
): List<PhoneticKeyboardContent> {
    if (!hasVsoCandidate(soggettiCount, oggettiCount, verbSentences, words)) return emptyList()
    val quizzes = mutableListOf<PhoneticKeyboardContent>()
    for (i in 0 until count) {
        var attempts = 0
        while (attempts < 10) {
            val sentenceResult = buildImperfectiveSentenceSync(
                soggettiCount = soggettiCount,
                oggettiCount = oggettiCount,
                random = random,
                verbSentences = verbSentences,
                words = words,
                verbs = verbs
            )
            val isDuplicate = quizzes.any { it.sentence == sentenceResult.hieroglyphs }
            if (isDuplicate && attempts < 9) {
                attempts++
                continue
            }
            val translation = when (languageCode) {
                "it" -> sentenceResult.translationIt
                "de" -> sentenceResult.translationDe
                "es" -> sentenceResult.translationEs
                "fr" -> sentenceResult.translationFr
                else -> sentenceResult.translationEn
            }
            quizzes.add(
                PhoneticKeyboardContent(
                    sentence = sentenceResult.hieroglyphs,
                    info = "${sentenceResult.transliteration} -> ${translation}",
                    reading = sentenceResult.transliteration.map { it.toString() },
                    isProperName = false,
                    canRotate = false
                )
            )
            break
        }
    }
    return quizzes
}

fun buildVsoRecapPhoneticKeyboardQuizzes(
    random: Random,
    languageCode: String,
    count: Int = 2,
    verbSentences: List<SentenceOptions> = DatabaseRepositoryImpl.verbSentences,
    words: List<Word> = DatabaseRepositoryImpl.words,
    verbs: List<Verb> = DatabaseRepositoryImpl.verbs
): List<PhoneticKeyboardContent> {
    val quizzes = mutableListOf<PhoneticKeyboardContent>()
    for (i in 0 until count) {
        val soggettiCount = 1
        val oggettiCount = random.nextInt(0, 2)
        if (!hasVsoCandidate(soggettiCount, oggettiCount, verbSentences, words)) continue
        var attempts = 0
        while (attempts < 10) {
            val sentenceResult = buildImperfectiveSentenceSync(
                soggettiCount = soggettiCount,
                oggettiCount = oggettiCount,
                random = random,
                verbSentences = verbSentences,
                words = words,
                verbs = verbs
            )
            val isDuplicate = quizzes.any { it.sentence == sentenceResult.hieroglyphs }
            if (isDuplicate && attempts < 9) {
                attempts++
                continue
            }
            val translation = when (languageCode) {
                "it" -> sentenceResult.translationIt
                "de" -> sentenceResult.translationDe
                "es" -> sentenceResult.translationEs
                "fr" -> sentenceResult.translationFr
                else -> sentenceResult.translationEn
            }
            quizzes.add(
                PhoneticKeyboardContent(
                    sentence = sentenceResult.hieroglyphs,
                    info = "${sentenceResult.transliteration} -> ${translation}",
                    reading = sentenceResult.transliteration.map { it.toString() },
                    isProperName = false,
                    canRotate = false
                )
            )
            break
        }
    }
    return quizzes
}





